import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import dayjs from "dayjs";
import { Layout } from "../../UI/Layout/Layout.tsx";
import { NavBar } from "../../UI/Layout/NavBar.tsx";
import { Footer } from "../../UI/Layout/Footer.tsx";
import { FullPageLoading } from "../../UI/FullPageLoading.tsx";
import { ErrorState } from "../../UI/ErrorState.tsx";
import { AdminMainContainer } from "../../UI/Admin/AdminMainContainer.tsx";
import { useBonificoOrder } from "../../ProductService/BonificoOrder/useBonificoOrder.ts";
import { BonificoOrderStatus } from "../../ProductService/BonificoOrder/BonificoOrderDTO.ts";
import { AllBonificoOrdersScreenPath } from "./AllBonificoOrdersScreen.tsx";
import { BonificoOrderForm } from "./BonificoOrderForm.tsx";
import { ExternalSettlementForm } from "./ExternalSettlementForm.tsx";
import { OrderKindTabs } from "./OrderKindTabs.tsx";
import { BonificoOrderStatusBadge } from "./BonificoOrderStatusBadge.tsx";

export { ConfirmBonificoDraftScreenPath } from "./confirmBonificoDraftPath.ts";

type DraftProcessing = "classic" | "externalSettlement";

const DRAFT_PROCESSING_LABELS: Record<DraftProcessing, string> = {
  classic: "Ordine classico",
  externalSettlement: "Saldo ordine esterno",
};

function ConfirmBonificoDraftScreenInner({ orderId }: { orderId: string }) {
  const navigate = useNavigate();
  const { order, isLoading, error } = useBonificoOrder(orderId);
  const [processing, setProcessing] = useState<DraftProcessing>("classic");
  const toOrders = () => navigate(AllBonificoOrdersScreenPath);

  if (isLoading) return <FullPageLoading />;

  return (
    <Layout>
      <AdminMainContainer>
        <NavBar showAdminSideBarHandle />
        <div className="flex-1 p-4 flex flex-col gap-6 max-w-3xl mx-auto w-full">
          <div className="flex items-center gap-4">
            <button
              onClick={toOrders}
              className="text-sm text-blue-600 hover:underline"
            >
              ← Torna agli ordini
            </button>
            <h1 className="font-bold text-2xl">Conferma bozza bonifico</h1>
          </div>

          {error || !order ? (
            <ErrorState />
          ) : order.status !== BonificoOrderStatus.Draft ? (
            <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3 text-sm">
              <span>Questo ordine non è più una bozza:</span>
              <BonificoOrderStatusBadge status={order.status} />
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600">
                Bozza compilata da{" "}
                <strong>
                  {order.customer.firstName} {order.customer.lastName}
                </strong>{" "}
                il {dayjs(order.createdAt).format("DD/MM/YYYY HH:mm")}. Scegli
                come processarla: i dati inseriti dal cliente restano
                precompilati e si possono correggere prima della conferma.
              </p>
              {order.valueDateUtc && (
                <p className="bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2 text-sm text-indigo-800">
                  Data valuta già registrata:{" "}
                  <strong>
                    {dayjs(order.valueDateUtc).format("DD/MM/YYYY")}
                  </strong>
                  . Alla conferma l'ordine passa direttamente in «Fattura da
                  emettere».
                </p>
              )}
              <OrderKindTabs
                labels={DRAFT_PROCESSING_LABELS}
                value={processing}
                onChange={setProcessing}
              />
              {processing === "classic" ? (
                <BonificoOrderForm
                  mode="confirm"
                  draft={order}
                  onSaved={toOrders}
                  onCancel={toOrders}
                />
              ) : (
                <ExternalSettlementForm
                  draft={order}
                  onSaved={toOrders}
                  onCancel={toOrders}
                />
              )}
            </>
          )}
        </div>
      </AdminMainContainer>
      <Footer />
    </Layout>
  );
}

export function ConfirmBonificoDraftScreen() {
  const { orderId } = useParams();
  if (!orderId) return null;
  return <ConfirmBonificoDraftScreenInner orderId={orderId} />;
}
